import { Latch, type FighterInputSource, type FighterIntent } from './fighterInput';
import { aims, screenStick } from './stick';

/** How far from where the thumb landed counts as pushing the stick all the way, in pixels. */
const STICK_RADIUS = 56;
/** Where the stick waits when nobody is holding it, as a fraction of its half of the screen. */
const RESTING = { x: 0.42, y: 0.62 };

/** Whether the thing the player points with is a finger, which is what decides the on-screen pad. */
export function coarsePointer(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

const button = (label: string, className: string): HTMLButtonElement => {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = `touch-button ${className}`;
  element.textContent = label;
  element.setAttribute('aria-label', label);
  return element;
};

/**
 * A phone's controls for a duel: a stick under the left thumb and the buttons under the right.
 *
 * The stick is drawn where the thumb lands rather than sitting in a fixed ring. On a screen with
 * nothing to feel for, a fixed stick means looking down to find it; one that appears under the
 * thumb can be grabbed anywhere in its half of the pad, which is what makes it playable without
 * watching your own hands. It keeps the pointer once it has it, so sliding off the pad and into a
 * corner holds that corner instead of dropping the fighter mid-stride.
 *
 * One stick, so it also points: a fighter faces the way it runs. A second stick for aiming is what
 * the pad and the headset have, and there is no room for it under a thumb that is already busy.
 *
 * The controls are the player's own device drawn over the scene, so they are DOM rather than
 * Babylon GUI — they are never seen from inside the headset, which has real sticks.
 */
export function createTouchFighterInput(parent: HTMLElement): FighterInputSource {
  const dash = new Latch();
  const attack = new Latch();
  let stick = { x: 0, z: 0 };
  let thumb: number | null = null;
  /** Fire is held down now, not just pressed: a spellcaster steers while it is. */
  let fireHeld = false;
  let base = { x: 0, y: 0 };

  const root = document.createElement('div');
  root.className = 'touch-controls';
  root.dataset.testid = 'touch-controls';

  const zone = document.createElement('div');
  zone.className = 'touch-stick-zone';
  zone.setAttribute('aria-label', 'Move');
  const ring = document.createElement('div');
  ring.className = 'touch-stick-ring';
  const knob = document.createElement('div');
  knob.className = 'touch-stick-knob';
  ring.append(knob);
  zone.append(ring);

  const buttons = document.createElement('div');
  buttons.className = 'touch-buttons';
  const dashButton = button('Dash', 'touch-dash');
  const attackButton = button('Fire', 'touch-attack');
  buttons.append(dashButton, attackButton);

  root.append(zone, buttons);
  parent.append(root);

  /**
   * Draws the stick where it is being held, or at its resting place when it is not.
   *
   * It waits somewhere visible rather than nowhere at all. A stick that only exists once a thumb
   * has already found it cannot be found in the first place: the buttons opposite it are plainly
   * there, so an absent stick reads as a missing control rather than a hidden one.
   */
  const drawStick = () => {
    const bounds = zone.getBoundingClientRect();
    const at =
      thumb !== null
        ? { x: base.x - bounds.left, y: base.y - bounds.top }
        : { x: bounds.width * RESTING.x, y: bounds.height * RESTING.y };
    ring.style.left = `${at.x}px`;
    ring.style.top = `${at.y}px`;
    knob.style.transform = `translate(${stick.x * STICK_RADIUS}px, ${-stick.z * STICK_RADIUS}px)`;
  };

  const onStickDown = (e: PointerEvent) => {
    if (thumb !== null) return;
    thumb = e.pointerId;
    base = { x: e.clientX, y: e.clientY };
    stick = { x: 0, z: 0 };
    zone.setPointerCapture(e.pointerId);
    zone.classList.add('held');
    drawStick();
    e.preventDefault();
  };
  const onStickMove = (e: PointerEvent) => {
    if (e.pointerId !== thumb) return;
    stick = screenStick(base, { x: e.clientX, y: e.clientY }, STICK_RADIUS);
    drawStick();
    e.preventDefault();
  };
  const onStickUp = (e: PointerEvent) => {
    if (e.pointerId !== thumb) return;
    thumb = null;
    stick = { x: 0, z: 0 };
    zone.classList.remove('held');
    drawStick();
  };

  zone.addEventListener('pointerdown', onStickDown);
  zone.addEventListener('pointermove', onStickMove);
  zone.addEventListener('pointerup', onStickUp);
  zone.addEventListener('pointercancel', onStickUp);

  /** A button keeps the pointer too: a thumb that slides off it still releases it. */
  const wire = (element: HTMLButtonElement, latch: Latch, held?: (down: boolean) => void) => {
    const down = (e: PointerEvent) => {
      element.setPointerCapture(e.pointerId);
      element.classList.add('held');
      latch.set(true);
      held?.(true);
      e.preventDefault();
    };
    const up = () => {
      element.classList.remove('held');
      latch.set(false);
      held?.(false);
    };
    element.addEventListener('pointerdown', down);
    element.addEventListener('pointerup', up);
    element.addEventListener('pointercancel', up);
    return () => {
      element.removeEventListener('pointerdown', down);
      element.removeEventListener('pointerup', up);
      element.removeEventListener('pointercancel', up);
    };
  };
  const unwire = [
    wire(dashButton, dash),
    wire(attackButton, attack, (down) => {
      fireHeld = down;
    }),
  ];

  // Turning the phone moves the stick's half of the screen out from under its resting place.
  const onResize = () => drawStick();
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  // A phone that is put down, or a call that arrives, must not leave the fighter running.
  const onBlur = () => {
    thumb = null;
    stick = { x: 0, z: 0 };
    dash.reset();
    attack.reset();
    fireHeld = false;
    zone.classList.remove('held');
    dashButton.classList.remove('held');
    attackButton.classList.remove('held');
    drawStick();
  };
  window.addEventListener('blur', onBlur);
  // The layout is settled by the time the duel starts, so the stick can take its place at once.
  drawStick();

  return {
    update() {},
    next(mode): FighterIntent {
      // A spellcaster's shot, as on a pad: while Fire is held the stick points the next shot
      // without walking, and once it flies the stick's side to side bends it. The phone's one
      // stick only ran, so the class's craft was out of reach there (client-input:bugs#6).
      const magicAim = !!mode?.steerableProjectile && fireHeld && !mode.projectileInFlight;
      const magicSteer = !!mode?.steerableProjectile && mode.projectileInFlight && fireHeld;
      const still = magicAim || magicSteer;
      return {
        moveX: still ? 0 : stick.x,
        moveZ: still ? 0 : stick.z,
        aimAt: null,
        // The one stick points as well as moves, once it is pushed past a drift.
        aimDir: !magicSteer && aims(stick.x, stick.z) ? { x: stick.x, z: stick.z } : null,
        dash: dash.take(),
        attack: attack.take(),
        steer: magicSteer ? stick.x : 0,
      };
    },
    dispose() {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      zone.removeEventListener('pointerdown', onStickDown);
      zone.removeEventListener('pointermove', onStickMove);
      zone.removeEventListener('pointerup', onStickUp);
      zone.removeEventListener('pointercancel', onStickUp);
      for (const off of unwire) off();
      root.remove();
    },
  };
}
